import React from 'react';
import { RouteProps } from 'react-router-dom';
import DocumentTitle from '@/utils/DocumentTitle';
import * as Sentry from '@sentry/react';
import { Can } from '@/hooks/useRbac';
import { SentryTracedRoute } from '@/modules/app/SentryRoute';
import { BreadcrumbType } from '../ConnectedBreadcrumbs';

export type LayoutRouteProps = RouteProps & {
  /** Componente a renderear en esta ruta. */
  component: React.ComponentType;
  /** Componente de layout usado para renderear esta ruta. */
  layout: React.ComponentType<{
    noSidebar?: boolean;
    breadCrumbs?: BreadcrumbType[];
    children?: React.ReactNode;
  }>;
  /** Este parámetro se pasa directamente al componente Layout.
   * Pensado para controlar si mostrar o no el sidebar (se muestra por defecto).
   */
  noSidebar?: boolean;
  /** Titulo del documento, para mostrar en la barra de titulo del navegador. */
  title?: string;
  /* Breadcrumbs para mostrar en el layout */
  breadCrumbs?: BreadcrumbType[];
  /** Define an rbac action name to check for permissions.
   * If the check passes, the route renders. */
  rbacAction?: string;
  /** Arguments to pass to the rbac permission check */
  rbacCheckArgs?: any;
  /** Forwarded to SentryTracedRoute: `false` skips the PostHog pageview
   * (redirect-only routes). */
  trackPageview?: boolean;
};

const LayoutRoute: React.FC<LayoutRouteProps> = Sentry.withSentryRouting(
  ({
    component: Component,
    layout: Layout,
    noSidebar,
    title,
    rbacAction,
    rbacCheckArgs,
    breadCrumbs,
    ...rest
  }) => (
    <SentryTracedRoute
      {...rest}
      render={(props) => (
        <Layout noSidebar={noSidebar} breadCrumbs={breadCrumbs}>
          <DocumentTitle title={title} />
          <Can rbacAction={rbacAction} rbacCheckArgs={rbacCheckArgs}>
            <div className="flex flex-col w-full grow">
              {rbacAction === 'debug:view' && (
                <div className="w-full bg-warning/70 text-warning-foreground py-3 px-5 sticky top-16 rounded-md z-10">
                  Estas viendo una página en desarrollo no accesible al público
                  general.
                </div>
              )}
              <Component {...props} />
            </div>
          </Can>
        </Layout>
      )}
    />
  )
);

export default LayoutRoute;
